/** Median of a non-empty list; the mean of the two middle values for an even count. */
export const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[middle]
        : (sorted[middle - 1] + sorted[middle]) / 2;
};
const time = (instant) => instant === undefined ? Number.NEGATIVE_INFINITY : Date.parse(instant);
const RECENT = 10;
export const bakeStats = (bakes) => {
    const byRecent = [...bakes].sort((a, b) => time(b.startedAt) - time(a.startedAt) || a.bakeId.localeCompare(b.bakeId));
    const name = (bake) => bake.recipe.name === undefined ? {} : { name: bake.recipe.name };
    const recipes = new Map();
    for (const bake of byRecent) {
        const known = recipes.get(bake.recipe.id);
        recipes.set(bake.recipe.id, {
            recipeId: bake.recipe.id,
            ...name(bake),
            ...(known?.name === undefined ? {} : { name: known.name }),
            count: (known?.count ?? 0) + 1,
        });
    }
    const durations = new Map();
    for (const bake of bakes) {
        for (const step of bake.steps) {
            const planned = step.baselineDurationS ?? step.plannedDurationS;
            if (planned === undefined || step.actualDurationS === undefined)
                continue;
            const entry = durations.get(step.operation) ?? { planned: [], recorded: [] };
            entry.planned.push(planned);
            entry.recorded.push(step.actualDurationS);
            durations.set(step.operation, entry);
        }
    }
    let marks = 0;
    const recorded = [];
    const model = [];
    const ratings = new Map();
    for (const bake of byRecent) {
        for (const observation of bake.observations) {
            if (observation.kind === 'doughReady') {
                marks += 1;
                const estimate = bake.steps.find((s) => s.id === observation.stepId)?.estimate;
                if (observation.elapsedInStepS !== undefined &&
                    estimate?.status === 'ok' &&
                    Number.isFinite(estimate.value)) {
                    recorded.push(observation.elapsedInStepS);
                    model.push(estimate.value);
                }
            }
            if (observation.rating !== undefined && Number.isFinite(observation.rating)) {
                const entry = ratings.get(bake.recipe.id) ?? { ...name(bake), values: [] };
                entry.values.push(observation.rating);
                ratings.set(bake.recipe.id, entry);
            }
        }
    }
    const finished = bakes.filter((b) => b.status === 'finished');
    const result = (bake) => bake.outcome === undefined || bake.outcome === 'unknown' ? 'unknown' : bake.outcome.result;
    const stages = new Map();
    for (const bake of bakes) {
        for (const step of bake.steps) {
            if (step.cooking && step.endedAt !== undefined) {
                stages.set(step.operation, (stages.get(step.operation) ?? 0) + 1);
            }
        }
    }
    return {
        counts: {
            total: bakes.length,
            finished: finished.length,
            abandoned: bakes.filter((b) => b.status === 'abandoned').length,
            active: bakes.filter((b) => b.status === 'active').length,
            results: {
                cooked: finished.filter((b) => result(b) === 'cooked').length,
                dough: finished.filter((b) => result(b) === 'dough').length,
                cookedAndDough: finished.filter((b) => result(b) === 'cookedAndDough').length,
                unknown: finished.filter((b) => result(b) === 'unknown').length,
            },
        },
        cookingStages: [...stages.entries()]
            .map(([operation, n]) => ({ operation, n }))
            .sort((a, b) => b.n - a.n || a.operation.localeCompare(b.operation)),
        recipes: [...recipes.values()].sort((a, b) => b.count - a.count),
        durations: [...durations.entries()]
            .map(([operation, { planned, recorded: actual }]) => ({
            operation,
            n: planned.length,
            medianPlannedS: median(planned),
            medianRecordedS: median(actual),
            medianDifferenceS: median(actual.map((value, i) => value - planned[i])),
        }))
            .sort((a, b) => b.n - a.n || a.operation.localeCompare(b.operation)),
        readiness: {
            marks,
            compared: recorded.length,
            ...(recorded.length === 0
                ? {}
                : {
                    medianDifferenceS: median(recorded.map((value, i) => value - model[i])),
                    medianRecordedS: median(recorded),
                    medianModelS: median(model),
                }),
        },
        ratings: [...ratings.entries()]
            .map(([recipeId, { name: recipeName, values }]) => ({
            recipeId,
            ...(recipeName === undefined ? {} : { name: recipeName }),
            average: values.reduce((sum, value) => sum + value, 0) / values.length,
            n: values.length,
        }))
            .sort((a, b) => b.average - a.average || b.n - a.n),
        recent: byRecent.slice(0, RECENT).map((bake) => {
            const durationS = bake.startedAt === undefined || bake.endedAt === undefined
                ? undefined
                : Math.round((Date.parse(bake.endedAt) - Date.parse(bake.startedAt)) / 1000);
            return {
                bakeId: bake.bakeId,
                recipeId: bake.recipe.id,
                ...name(bake),
                status: bake.status,
                ...(bake.startedAt === undefined ? {} : { startedAt: bake.startedAt }),
                ...(bake.endedAt === undefined ? {} : { endedAt: bake.endedAt }),
                ...(durationS === undefined ? {} : { durationS }),
            };
        }),
    };
};
//# sourceMappingURL=stats.js.map